import React, { useState } from 'react';
import {
  Shield,
  Scale,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Globe,
  Eye,
  Sliders,
  Sparkles,
  RefreshCw,
  Search,
  Check,
  Power,
  ChevronRight,
  Database,
  Tag,
  AlertCircle,
  HelpCircle,
  ExternalLink,
  BookOpen,
  Receipt,
  RotateCcw,
  Clock,
  UserCheck,
  Zap,
  Cpu,
  Layers,
  ArrowRight
} from 'lucide-react';
import { AppView } from '../../types';
import {
  CCO_EXECUTIVE_METRICS,
  LEGAL_AUTHORITIES,
  LEGAL_OBLIGATIONS,
  PROCESSING_ACTIVITIES_ROPA,
  PRIVACY_RIGHTS_REQUESTS,
  TRACKING_TECHNOLOGIES_REGISTRY,
  SUBPROCESSORS_REGISTRY,
  FINANCIAL_REGULATORY_ACTIVITIES,
  CONTEXTUAL_DISCLOSURES,
  AI_SYSTEM_GOVERNANCE,
  ACCESSIBILITY_AUDIT_RECORDS,
  RETENTION_POLICIES,
  LEGAL_HOLDS,
  MARKET_COMPLIANCE_PROFILES,
  LEGAL_DOCUMENTS,
  REGULATORY_CHANGES,
  COMPLIANCE_INCIDENTS
} from '../../services/compliance/complianceMockData';
import { complianceEngine } from '../../services/compliance/complianceEngine';
import { MarketComplianceProfile, PrivacyRightsRequest } from '../../services/compliance/complianceTypes';

type CCOTab =
  | 'overview'
  | 'markets'
  | 'privacy-ropa'
  | 'dsar'
  | 'cookies'
  | 'fintech-boundary'
  | 'subprocessors'
  | 'ai-governance'
  | 'accessibility'
  | 'retention-holds'
  | 'documents'
  | 'changes-incidents'
  | 'kill-switches';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const ChiefComplianceOfficerCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<CCOTab>('overview');
  const [selectedMarket, setSelectedMarket] = useState<MarketComplianceProfile>(MARKET_COMPLIANCE_PROFILES[0]);
  const [testCountryCode, setTestCountryCode] = useState('FR');
  const [testEligibilityResult, setTestEligibilityResult] = useState<any>(null);
  const [retentionDryRunResult, setRetentionDryRunResult] = useState<any>(null);
  const [killSwitchStates, setKillSwitchStates] = useState({
    paidCheckoutUS: true,
    aiAssistantGlobal: true,
    transferComparator: true,
    personalizedAdsEEA: false
  });
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const handleTestMarketEligibility = (e: React.FormEvent) => {
    e.preventDefault();
    const res = complianceEngine.checkMarketPaidEligibility(testCountryCode);
    setTestEligibilityResult(res);
  };

  const handleRunRetentionDryRun = () => {
    const res = complianceEngine.executeRetentionDryRun();
    setRetentionDryRunResult(res);
    setActionNotice('Retention dry-run simulation executed successfully. Zero data deleted.');
    setTimeout(() => setActionNotice(null), 5000);
  };

  const handleToggleKillSwitch = (key: keyof typeof killSwitchStates, label: string) => {
    setKillSwitchStates((prev) => {
      const next = !prev[key];
      setActionNotice(`Compliance Gate "${label}" is now ${next ? 'ENABLED' : 'DISABLED'}.`);
      setTimeout(() => setActionNotice(null), 5000);
      return { ...prev, [key]: next };
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#1E3A8A] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Scale className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Legal & Regulatory Governance</span>
            <span>/</span>
            <span className="text-white font-bold">Chief Compliance Officer Command Center (Prompt 22)</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                Global Legal, Privacy & Compliance OS
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                GDPR, UK GDPR, CCPA/CPRA, FinTech non-advisory boundaries, KYC/AML governance, EU AI Act, WCAG 2.2 AA accessibility, and market compliance launch gates.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onNavigateToView('legal-portal')}
                className="px-4 py-2.5 bg-blue-900/60 hover:bg-blue-900 border border-blue-400/40 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <BookOpen className="w-4 h-4 text-[#38BDF8]" />
                <span>Public Legal Portal</span>
              </button>

              <div className="px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border bg-emerald-950/60 border-emerald-500/40 text-emerald-300">
                <Shield className="w-4 h-4" />
                <span>Compliance Posture: VERIFIED</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Sub Navigation Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'overview', label: 'Executive Posture', icon: Scale },
              { id: 'markets', label: `Markets & Launch Gates (${MARKET_COMPLIANCE_PROFILES.length})`, icon: Globe },
              { id: 'privacy-ropa', label: `RoPA & Lawful Bases (${PROCESSING_ACTIVITIES_ROPA.length})`, icon: Database },
              { id: 'dsar', label: `Data Subject Rights (${PRIVACY_RIGHTS_REQUESTS.length})`, icon: UserCheck },
              { id: 'cookies', label: `Trackers & CMP (${TRACKING_TECHNOLOGIES_REGISTRY.length})`, icon: Eye },
              { id: 'fintech-boundary', label: 'FinTech Boundaries & Disclosures', icon: Shield },
              { id: 'subprocessors', label: `Subprocessors (${SUBPROCESSORS_REGISTRY.length})`, icon: Layers },
              { id: 'ai-governance', label: 'AI Act Governance', icon: Cpu },
              { id: 'accessibility', label: 'WCAG 2.2 AA & EAA', icon: CheckCircle2 },
              { id: 'retention-holds', label: 'Retention & Legal Holds', icon: Clock },
              { id: 'documents', label: `Legal Documents (${LEGAL_DOCUMENTS.length})`, icon: FileText },
              { id: 'changes-incidents', label: 'Regulatory Changes & Incidents', icon: AlertTriangle },
              { id: 'kill-switches', label: 'Emergency Legal Kill Switches', icon: Power }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as CCOTab)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {actionNotice && (
            <div className="p-4 mb-6 bg-blue-50 border border-blue-200 text-blue-950 rounded-xl text-sm font-bold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />
              <span>{actionNotice}</span>
            </div>
          )}

          {/* TAB 1: EXECUTIVE POSTURE */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Policy Statement */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-4">
                <Shield className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-blue-950 font-bold text-base mb-1">
                    TECHNICALLY POSSIBLE ≠ LEGALLY PERMITTED & ZERO UNSUPPORTED CLAIMS
                  </h3>
                  <p className="text-blue-900 text-sm leading-relaxed">
                    YamooPay operates with strict regulatory boundary separation: Calculators provide mathematical estimates, not regulated credit decisions or investment advice. Public free tools require zero unnecessary KYC document collection. Commercial monetization remains gated by country-specific legal certification.
                  </p>
                </div>
              </div>

              {/* KPI Matrix */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Monitored Jurisdictions</div>
                  <div className="text-3xl font-black text-[#0F172A]">{CCO_EXECUTIVE_METRICS.totalMonitoredMarkets}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">7 Paid Certified • 41 Free/Blocked</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Open Privacy Requests (DSAR)</div>
                  <div className="text-3xl font-black text-[#1E40AF]">{CCO_EXECUTIVE_METRICS.openPrivacyRequests}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">0 Overdue • 100% 30-Day SLA</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Active Subprocessors</div>
                  <div className="text-3xl font-black text-[#0F172A]">{CCO_EXECUTIVE_METRICS.activeSubprocessorsCount}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">100% Signed DPAs & SCCs</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Accessibility Conformance</div>
                  <div className="text-3xl font-black text-[#059669]">WCAG 2.2 AA</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">0 Critical Blockers (EAA Ready)</div>
                </div>
              </div>

              {/* Core Legal Authorities Table */}
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-3">Primary Governing Legal Authorities</h3>
                <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      <tr>
                        <th className="px-4 py-3 font-bold text-[#475569]">Authority & Code</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Jurisdiction</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Governing Body</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Next Review</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {LEGAL_AUTHORITIES.map((auth) => (
                        <tr key={auth.id} className="hover:bg-[#F8FAFC]">
                          <td className="px-4 py-3">
                            <div className="font-bold text-[#0F172A]">{auth.name}</div>
                            <span className="font-mono text-xs text-[#1E40AF]">{auth.code}</span>
                          </td>
                          <td className="px-4 py-3 text-xs font-medium text-[#475569]">{auth.jurisdiction}</td>
                          <td className="px-4 py-3 text-xs text-[#64748B]">{auth.governingBody}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                              {auth.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-[#64748B]">{auth.nextReviewAt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: MARKETS & LAUNCH GATES */}
          {activeTab === 'markets' && (
            <div className="space-y-8">
              {/* Interactive Market Eligibility Simulator */}
              <div className="p-5 bg-[#F8FAFC] border border-[#CBD5E1] rounded-2xl space-y-4">
                <h3 className="text-base font-black text-[#0F172A] flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#1E40AF]" />
                  <span>Market Compliance Launch Gate Simulator</span>
                </h3>
                <p className="text-xs text-[#64748B]">
                  Test whether a target country is legally authorized for commercial paid monetization and checkout flows.
                </p>

                <form onSubmit={handleTestMarketEligibility} className="flex flex-wrap gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Enter ISO Country Code (e.g. US, FR, BR, KP)"
                    value={testCountryCode}
                    onChange={(e) => setTestCountryCode(e.target.value.toUpperCase())}
                    className="px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs font-mono uppercase focus:ring-2 focus:ring-[#1E40AF] focus:outline-hidden"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
                  >
                    Evaluate Market Gate
                  </button>
                </form>

                {testEligibilityResult && (
                  <div className={`p-4 rounded-xl border text-xs space-y-1 ${
                    testEligibilityResult.eligible
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-950'
                      : 'bg-amber-50 border-amber-200 text-amber-950'
                  }`}>
                    <div className="flex items-center gap-2 font-bold text-sm">
                      {testEligibilityResult.eligible ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      <span>Gate State: {testEligibilityResult.gateState}</span>
                    </div>
                    <p><strong>Operating Entity:</strong> {testEligibilityResult.operatingEntity}</p>
                    <p><strong>Compliance Verdict:</strong> {testEligibilityResult.reason}</p>
                  </div>
                )}
              </div>

              {/* Global Market Profiles Table */}
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-3">Global Market Compliance Matrix</h3>
                <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      <tr>
                        <th className="px-4 py-3 font-bold text-[#475569]">Market</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Operating Entity</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Privacy Pack</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Free Status</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Paid Monetization Gate</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Providers</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {MARKET_COMPLIANCE_PROFILES.map((m) => (
                        <tr key={m.countryCode} className="hover:bg-[#F8FAFC]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 font-bold text-[#0F172A]">
                              <span className="text-lg">{m.flagEmoji}</span>
                              <span>{m.countryName} ({m.countryCode})</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#64748B]">{m.operatingEntity}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#1E40AF]">{m.privacyPack}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                              m.freeCalculatorsStatus === 'AVAILABLE' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {m.freeCalculatorsStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                              m.paidMonetizationGate === 'LEGAL_READY_LIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {m.paidMonetizationGate}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-[#64748B]">
                            {m.supportedPaymentProviders.length > 0 ? m.supportedPaymentProviders.join(', ') : 'None'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: RoPA & LAWFUL BASES */}
          {activeTab === 'privacy-ropa' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Article 30 Record of Processing Activities (RoPA)</h3>
                <p className="text-sm text-[#64748B]">
                  Immutable inventory of personal data categories, purposes, lawful bases, and transfer mechanisms.
                </p>
              </div>

              <div className="space-y-4">
                {PROCESSING_ACTIVITIES_ROPA.map((ropa) => (
                  <div key={ropa.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{ropa.name}</h4>
                        <span className="text-xs font-mono text-[#1E40AF]">{ropa.id}</span>
                      </div>
                      <span className="px-2.5 py-1 bg-purple-100 text-purple-800 font-bold text-xs rounded-full">
                        Lawful Basis: {ropa.lawfulBasis}
                      </span>
                    </div>

                    <p className="text-xs text-[#475569] leading-relaxed">{ropa.purpose}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">Data Categories:</strong>
                        <span className="text-[#64748B]">{ropa.dataCategories.join(', ')}</span>
                      </div>
                      <div>
                        <strong className="block text-[#0F172A]">Processors Involved:</strong>
                        <span className="text-[#64748B]">{ropa.processors.join(', ')}</span>
                      </div>
                      <div>
                        <strong className="block text-[#0F172A]">Retention Period:</strong>
                        <span className="text-[#059669] font-medium">{ropa.retentionPeriod}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: DATA SUBJECT RIGHTS (DSAR) */}
          {activeTab === 'dsar' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Data Subject Privacy Rights Requests (DSAR)</h3>
                <p className="text-sm text-[#64748B]">
                  30-day statutory SLA tracking for GDPR, UK GDPR, and California CPRA requests.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Tracking & User</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Right Type</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Jurisdiction</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Deadline</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {PRIVACY_RIGHTS_REQUESTS.map((req) => (
                      <tr key={req.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3">
                          <div className="font-bold text-[#0F172A]">{req.requesterEmail}</div>
                          <span className="font-mono text-xs text-[#1E40AF]">{req.trackingCode}</span>
                        </td>
                        <td className="px-4 py-3 font-bold text-xs text-[#0F172A]">{req.rightType}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{req.jurisdiction}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#059669]">
                          {new Date(req.statutoryDeadline).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            req.status === 'FULFILLED' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {req.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: TRACKERS & CMP */}
          {activeTab === 'cookies' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Tracking Technology Registry & Consent Governance</h3>
                <p className="text-sm text-[#64748B]">
                  Strict pre-consent blocking for non-essential cookies and automated Global Privacy Control (GPC) signal detection.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TRACKING_TECHNOLOGIES_REGISTRY.map((trk) => (
                  <div key={trk.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base font-mono text-[#0F172A]">{trk.name}</h4>
                        <span className="text-xs text-[#64748B]">{trk.provider}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {trk.category}
                      </span>
                    </div>

                    <p className="text-xs text-[#475569]">{trk.purpose}</p>

                    <div className="text-[11px] text-[#64748B] space-y-0.5 pt-2 border-t border-[#F1F5F9]">
                      <div><strong>Storage Type:</strong> {trk.technologyType}</div>
                      <div><strong>Lifespan:</strong> {trk.lifespan}</div>
                      <div><strong>Consent Needed In:</strong> {trk.requiresConsentIn.length > 0 ? trk.requiresConsentIn.join(', ') : 'Exempt (Strictly Essential)'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 6: FINTECH BOUNDARIES & DISCLOSURES */}
          {activeTab === 'fintech-boundary' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Financial Regulatory Boundaries & Non-Advisory Engine</h3>
                <p className="text-sm text-[#64748B]">
                  Hard walls preventing YamooPay from operating as an unlicensed money transmitter, broker, or lender.
                </p>
              </div>

              <div className="space-y-4">
                {FINANCIAL_REGULATORY_ACTIVITIES.map((act) => (
                  <div key={act.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{act.featureName}</h4>
                        <span className="text-xs font-mono text-[#1E40AF]">{act.activityType}</span>
                      </div>
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                        act.regulatoryStatus === 'PROHIBITED_CURRENT_MODEL'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {act.regulatoryStatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                      <div>Holds Funds: <strong>{act.holdsFunds ? 'YES (Prohibited)' : 'NO (Safe)'}</strong></div>
                      <div>Initiates Transfers: <strong>{act.initiatesTransfers ? 'YES (Prohibited)' : 'NO (Safe)'}</strong></div>
                      <div>Credit Decision: <strong>{act.providesCreditDecision ? 'YES (Prohibited)' : 'NO (Safe)'}</strong></div>
                      <div>Investment Advice: <strong>{act.providesInvestmentAdvice ? 'YES (Prohibited)' : 'NO (Safe)'}</strong></div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-3">Contextual Financial Disclosures Registry</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CONTEXTUAL_DISCLOSURES.map((disc) => (
                    <div key={disc.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-sm text-[#0F172A]">{disc.title}</h4>
                        <span className="font-mono text-xs text-[#1E40AF]">{disc.code}</span>
                      </div>
                      <p className="text-xs text-[#475569] leading-relaxed italic bg-slate-50 p-3 rounded-lg border border-slate-200">
                        "{disc.textPlainEn}"
                      </p>
                      <div className="text-[11px] text-[#64748B]">
                        <strong>Placement:</strong> {disc.placementSurface}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: SUBPROCESSORS */}
          {activeTab === 'subprocessors' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Third-Party Subprocessor & International Transfer Registry</h3>
                <p className="text-sm text-[#64748B]">
                  Certified cloud infrastructure, payment processors, and AI model providers with signed DPAs and valid transfer mechanisms.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SUBPROCESSORS_REGISTRY.map((sub) => (
                  <div key={sub.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{sub.name}</h4>
                        <span className="text-xs text-[#64748B] font-mono">{sub.securityTier}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {sub.status}
                      </span>
                    </div>

                    <p className="text-xs text-[#475569]">{sub.serviceProvided}</p>

                    <div className="text-xs text-[#64748B] space-y-1 pt-2 border-t border-[#F1F5F9]">
                      <div><strong>Locations:</strong> {sub.processingLocations.join(', ')}</div>
                      <div><strong>Transfer Mechanism:</strong> {sub.transferMechanism}</div>
                      <div><strong>DPA Signed:</strong> <strong className="text-emerald-700">{sub.dpaSigned ? 'YES (Verified)' : 'NO'}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 8: AI GOVERNANCE */}
          {activeTab === 'ai-governance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">EU AI Act & Global AI Governance Engine</h3>
                <p className="text-sm text-[#64748B]">
                  Article 50 transparency badges, non-human identification, and prohibited creditworthiness profiling filters.
                </p>
              </div>

              <div className="space-y-4">
                {AI_SYSTEM_GOVERNANCE.map((sys) => (
                  <div key={sys.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{sys.systemName}</h4>
                        <span className="text-xs text-[#64748B]">{sys.purpose}</span>
                      </div>
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                        sys.status === 'AUTHORIZED' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {sys.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">AI Act Risk Class:</strong>
                        <span className="font-mono text-[#1E40AF] font-bold">{sys.aiActRiskClass}</span>
                      </div>
                      <div>
                        <strong className="block text-[#0F172A]">Chatbot Transparency Badge:</strong>
                        <span className="text-[#059669] font-bold">{sys.transparencyNoticeActive ? 'ACTIVE & VISIBLE' : 'DISABLED'}</span>
                      </div>
                      <div>
                        <strong className="block text-[#0F172A]">Decision Impact:</strong>
                        <span className="text-[#475569]">{sys.decisionImpact}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 9: ACCESSIBILITY */}
          {activeTab === 'accessibility' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Accessibility Conformance (WCAG 2.2 AA & European Accessibility Act)</h3>
                <p className="text-sm text-[#64748B]">
                  Continuous testing across screen readers, keyboard focus traps, high contrast, and dynamic recalculation announcements.
                </p>
              </div>

              <div className="space-y-3">
                {ACCESSIBILITY_AUDIT_RECORDS.map((aud) => (
                  <div key={aud.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-sm text-[#0F172A]">{aud.componentOrFlow}</h4>
                        <span className="text-xs font-mono text-[#1E40AF]">{aud.wcagCriterion} ({aud.level})</span>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {aud.status}
                      </span>
                    </div>
                    <div className="text-xs text-[#64748B] flex justify-between pt-2 border-t border-[#F1F5F9]">
                      <span>Method: <strong>{aud.testingMethod}</strong></span>
                      <span>Owner: <strong>{aud.remediationOwner}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 10: RETENTION & LEGAL HOLDS */}
          {activeTab === 'retention-holds' && (
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">Data Retention Policies & Legal Holds</h3>
                  <p className="text-sm text-[#64748B]">
                    Automated data lifecycle management respecting statutory accounting holds.
                  </p>
                </div>

                <button
                  onClick={handleRunRetentionDryRun}
                  className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Execute Retention Dry-Run</span>
                </button>
              </div>

              {retentionDryRunResult && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2 text-xs text-blue-950">
                  <h4 className="font-bold text-sm">Dry-Run Simulation Output:</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {retentionDryRunResult.actionsSummary.map((act: string, i: number) => (
                      <li key={i}>{act}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Record Category</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Retention Period</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Trigger Event</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Legal Basis</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Legal Holds</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {RETENTION_POLICIES.map((ret) => (
                      <tr key={ret.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-bold text-[#0F172A] text-xs">{ret.recordCategory}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#059669] font-bold">{ret.retentionPeriod}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{ret.triggerEvent}</td>
                        <td className="px-4 py-3 text-xs text-[#475569]">{ret.legalBasis}</td>
                        <td className="px-4 py-3">
                          {ret.legalHoldActiveCount > 0 ? (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-bold rounded">
                              1 ACTIVE HOLD
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-bold rounded">
                              0 Holds
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 11: LEGAL DOCUMENTS */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Published Legal Documents & Version Registry</h3>
                <p className="text-sm text-[#64748B]">
                  Immutable version control with legal reviewer approvals and public change summaries.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LEGAL_DOCUMENTS.map((doc) => (
                  <div key={doc.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{doc.title}</h4>
                        <span className="text-xs text-[#1E40AF] font-mono">Version {doc.version}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {doc.status}
                      </span>
                    </div>

                    <p className="text-xs text-[#475569] leading-relaxed">
                      <strong>Changes:</strong> {doc.summaryOfChanges}
                    </p>

                    <div className="text-xs text-[#64748B] pt-2 border-t border-[#F1F5F9] flex justify-between">
                      <span>Effective: <strong>{new Date(doc.effectiveFrom).toLocaleDateString()}</strong></span>
                      <span>Reviewer: <strong>{doc.legalReviewer}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 12: REGULATORY CHANGES & INCIDENTS */}
          {activeTab === 'changes-incidents' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Regulatory Change Management Tracker</h3>
                <p className="text-sm text-[#64748B]">
                  Continuous monitoring of global legislative enactments, ICO updates, and CPPA rulemaking.
                </p>
              </div>

              <div className="space-y-4">
                {REGULATORY_CHANGES.map((chg) => (
                  <div key={chg.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-sm text-[#0F172A]">{chg.title}</h4>
                        <span className="text-xs font-mono text-[#1E40AF]">{chg.jurisdiction} • {chg.authority}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {chg.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#475569]">{chg.summary}</p>
                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] text-xs text-[#0F172A]">
                      <strong>Impact Analysis & Action:</strong> {chg.actionRequired}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 13: EMERGENCY KILL SWITCHES */}
          {activeTab === 'kill-switches' && (
            <div className="space-y-6">
              <div className="p-6 bg-white border border-[#CBD5E1] rounded-2xl space-y-6 shadow-xs">
                <div>
                  <h3 className="text-xl font-black text-[#0F172A] flex items-center gap-2">
                    <Power className="w-5 h-5 text-red-600" />
                    <span>Emergency Legal Circuit Breakers</span>
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Instant administrative overrides to isolate specific high-risk jurisdictions or features during active regulatory inquiries.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    { key: 'paidCheckoutUS', label: 'Commercial Checkout & Billing (US Market)', desc: 'Instantly suspends paid checkout sessions in the United States while preserving existing subscriber access.' },
                    { key: 'aiAssistantGlobal', label: 'AI Assistant & RAG Engine (Global)', desc: 'Halts all generative conversational models and redirects users to static formula definitions.' },
                    { key: 'transferComparator', label: 'Money Transfer Provider Comparator Links', desc: 'Conceals outbound affiliate referral links during provider contract reviews.' },
                    { key: 'personalizedAdsEEA', label: 'Cross-Context Personalized Ads (EEA)', desc: 'Forces non-personalized contextual ads only across all European economic territory.' },
                  ].map((sw) => {
                    const isEngaged = (killSwitchStates as any)[sw.key];
                    return (
                      <div key={sw.key} className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                          <h4 className="font-bold text-sm text-[#0F172A]">{sw.label}</h4>
                          <p className="text-xs text-[#64748B] mt-0.5">{sw.desc}</p>
                        </div>

                        <button
                          onClick={() => handleToggleKillSwitch(sw.key as any, sw.label)}
                          className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer shrink-0 ${
                            isEngaged
                              ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                              : 'bg-red-700 hover:bg-red-800 text-white'
                          }`}
                        >
                          {isEngaged ? 'FEATURE ACTIVE' : 'FEATURE BLOCKED'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
